import { Shield, Fingerprint, RefreshCw, Plug, Eye, ArrowRight } from "lucide-react";

const enterpriseCapabilities = [
  {
    icon: Fingerprint,
    title: "One-Click Team Access",
    desc: "Your team logs in with existing company credentials. No new passwords, no IT tickets. Everyone's in within seconds.",
    badge: "Zero Friction",
  },
  {
    icon: RefreshCw,
    title: "Automatic User Management",
    desc: "Staff joins or leaves your org? ResiliencyHUB access updates itself — instantly. Zero manual work for your IT team.",
    badge: "Hands-Free",
  },
  {
    icon: Plug,
    title: "Connects to Your Existing Tools",
    desc: "Import from Workday, ServiceNow, or any system your org already runs. No duplicate data entry. Plans stay in sync.",
    badge: "Plug & Play",
  },
  {
    icon: Eye,
    title: "One View Across All Regions",
    desc: "Each business unit manages their own BCM program. Leadership gets a single, unified view of resilience across the entire enterprise.",
    badge: "Full Picture",
  },
];

export default function EnterpriseFederationSection() {
  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-slate-950 relative overflow-hidden" data-testid="enterprise-federation-section">
      {/* Grid bg */}
      <div className="absolute inset-0" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)",
        backgroundSize: "32px 32px"
      }} />
      <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-sky-600/8 rounded-full blur-[150px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-semibold mb-6">
            <Shield className="w-4 h-4" />
            Built for Enterprise
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-5">
            Fits Into Your Organization.{" "}
            <span className="text-emerald-400">
              Not the Other Way Around.
            </span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Start with an 8-person pilot. Roll out to 10,000 users without changing a thing.
            Your existing identity, HR, and IT systems stay exactly where they are — ResiliencyHUB plugs right in.
          </p>
        </div>

        {/* Cards — unified sky/emerald palette */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {enterpriseCapabilities.map((f, i) => (
            <div
              key={i}
              data-testid={`federation-card-${i}`}
              className="bg-slate-900/60 border border-slate-800 backdrop-blur-xl rounded-2xl p-6 transition-all duration-300 hover:border-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/5"
            >
              <div className="w-11 h-11 bg-emerald-500/15 rounded-xl flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-sky-500/10 text-sky-400 border border-sky-500/20 mb-3">
                {f.badge}
              </div>
              <h4 className="text-lg font-bold text-white mb-2">{f.title}</h4>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Scale path — single teal/emerald progression */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 sm:p-10">
          <div className="text-center mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Grow at Your Pace</h3>
            <p className="text-sm text-slate-500">Start free. Scale when ready. No re-platforming, no migrations.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { phase: "Pilot", users: "8 users", detail: "Free with approval", opacity: "opacity-50" },
              { phase: "Team", users: "50 users", detail: "Single sign-on ready", opacity: "opacity-65" },
              { phase: "Department", users: "500 users", detail: "Auto user sync", opacity: "opacity-80" },
              { phase: "Enterprise", users: "10,000+", detail: "Unified visibility", opacity: "opacity-100" },
            ].map((tier, i) => (
              <div key={i} className="text-center rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5 text-emerald-400">
                <p className={`text-xs font-bold tracking-[0.15em] uppercase ${tier.opacity} mb-1`}>{tier.phase}</p>
                <p className="text-2xl font-black tracking-tight">{tier.users}</p>
                <p className={`text-xs ${tier.opacity} mt-1`}>{tier.detail}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-2 mt-6">
            {[0,1,2].map(i => (
              <ArrowRight key={i} className="w-4 h-4 text-slate-600" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
